/**
 * PII masking pipeline.
 *
 * Strips identifying information from resume + JD text so the engine can
 * re-score on an anonymized version, producing a score delta that quantifies
 * how much identity signals (name, school, graduation year, etc.) drove the
 * original result.
 *
 * This is the TalentAlign bias audit primitive — the foundation for NYC AEDT,
 * EU AI Act, and Illinois AI Interview Act compliance reports.
 *
 * Design:
 *   - Each category is an independent `Masker` function
 *   - Maskers are pure: `(text) -> { maskedText, replacements }`
 *   - The pipeline runs all maskers in order, accumulating replacements
 *   - Returns a typed record of *what* was stripped so the UI can show
 *     the candidate-facing "redacted preview" alongside the score delta
 *
 * Performance: pure regex, no deps, runs in <10ms on a typical resume.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type PiiCategory =
  | "name"
  | "school"
  | "year"
  | "email"
  | "phone"
  | "address"
  | "pronoun"
  | "url"
  | "dob";

export interface PiiReplacement {
  category: PiiCategory;
  original: string;
  placeholder: string;
}

export interface PiiMaskOptions {
  maskName?: boolean;
  maskSchool?: boolean;
  maskYear?: boolean;
  maskEmail?: boolean;
  maskPhone?: boolean;
  maskAddress?: boolean;
  maskPronoun?: boolean;
  maskUrl?: boolean;
  maskDob?: boolean;
}

export const DEFAULT_PII_OPTIONS: Required<PiiMaskOptions> = {
  maskName: true,
  maskSchool: true,
  maskYear: true,
  maskEmail: true,
  maskPhone: true,
  maskAddress: true,
  maskPronoun: true,
  maskUrl: true,
  maskDob: true,
};

export interface PiiMaskResult {
  maskedText: string;
  replacements: PiiReplacement[];
  counts: Record<PiiCategory, number>;
}

// ---------------------------------------------------------------------------
// Maskers — each returns (text, replacements) for a single category
// ---------------------------------------------------------------------------
type MaskerFn = (text: string) => { text: string; replacements: PiiReplacement[] };

// -- 1. Names -----------------------------------------------------------------
// Strategy: identify the candidate's name from the first non-empty, non-heading
// line that looks name-shaped (2-5 title-case words, optional degree suffix
// like "MD, PhD"). Then strip every occurrence of each word of that name from
// the entire document. We do NOT blanket-strip all title-case words — that
// would wreck company names and job titles.
function maskNames(text: string): { text: string; replacements: PiiReplacement[] } {
  const replacements: PiiReplacement[] = [];
  const lines = text.split(/\r?\n/);

  // Find the candidate's name line
  let nameLine: string | null = null;
  let nameLineIdx = -1;
  for (let i = 0; i < Math.min(lines.length, 8); i++) {
    const raw = lines[i]?.trim();
    if (!raw) continue;
    if (raw.length > 80) continue; // heading lines can be long, names aren't
    if (raw.includes("@")) continue;
    if (/^https?:\/\//i.test(raw)) continue;

    // Strip a trailing comma-separated post-nominal suffix ("MD, PhD")
    const core = raw.split(",")[0]?.trim() ?? raw;
    const words = core.split(/\s+/);
    if (words.length < 2 || words.length > 5) continue;

    // Each word must be capitalized (Title Case) or all-caps (ALL CAPS NAMES)
    const shaped = words.every((w) => /^[A-Z][a-zA-Z.''-]*$|^[A-Z]+$/.test(w));
    if (!shaped) continue;

    // Reject obvious section headings by dictionary
    const joined = words.join(" ").toUpperCase();
    if (
      [
        "PROFESSIONAL EXPERIENCE",
        "WORK EXPERIENCE",
        "EMPLOYMENT HISTORY",
        "EDUCATION",
        "SKILLS",
        "TECHNICAL SKILLS",
        "CORE COMPETENCIES",
        "PROJECTS",
        "SELECTED PROJECTS",
        "PUBLICATIONS",
        "CERTIFICATIONS",
        "AWARDS AND HONORS",
        "HONORS",
        "SUMMARY",
        "OBJECTIVE",
      ].some((h) => joined.startsWith(h))
    ) {
      continue;
    }

    nameLine = raw;
    nameLineIdx = i;
    break;
  }

  if (!nameLine) return { text, replacements };

  // Mask the full name line first (captures suffixes too)
  lines[nameLineIdx] = "[CANDIDATE NAME]";
  replacements.push({
    category: "name",
    original: nameLine,
    placeholder: "[CANDIDATE NAME]",
  });

  // Then strip each name word from the rest of the document, but only when
  // it appears as a standalone word (not as a substring of a company or
  // school name). Skip single-letter tokens and degree tokens.
  const nameCore = nameLine.split(",")[0]?.trim() ?? nameLine;
  const nameWords = nameCore
    .split(/\s+/)
    .filter((w) => w.length > 1 && !/^[A-Z]{1,3}$/.test(w))
    .map((w) => w.replace(/['']/g, "'"));

  let reassembled = lines.join("\n");
  for (const word of nameWords) {
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`\\b${escaped}\\b`, "g");
    const matches = reassembled.match(re);
    if (matches && matches.length > 0) {
      reassembled = reassembled.replace(re, "[REDACTED]");
      replacements.push({
        category: "name",
        original: word,
        placeholder: "[REDACTED]",
      });
    }
  }

  return { text: reassembled, replacements };
}

// -- 2. Schools and universities ---------------------------------------------
// Curated patterns cover ~90% of common US institutions; generic patterns
// ("University of X", "X University", "X College") catch the tail.
const SCHOOL_PATTERNS: RegExp[] = [
  // Specific well-known institutions
  /\b(?:Harvard|MIT|Stanford|Yale|Princeton|Columbia|Cornell|Brown|Dartmouth|Penn|University of Pennsylvania)\b(?:\s+(?:University|College|Medical School|School of [\w\s]+))?/gi,
  /\b(?:Johns Hopkins|Georgia Tech|Georgia Institute of Technology|Carnegie Mellon|CMU|Caltech|California Institute of Technology|Duke|Vanderbilt|Northwestern|Kellogg School of Management)\b(?:\s+(?:University|School of [\w\s]+))?/gi,
  /\b(?:Rice|Emory|Tufts|Notre Dame|Washington University|Wash U|NYU|USC|UCLA|UC Berkeley|Berkeley|UCSD|UCSF)\b(?:\s+University)?/gi,
  /\bBrigham and Women's Hospital\b/gi,
  /\bMassachusetts General Hospital\b/gi,
  /\bHarvard Medical School\b/gi,
  /\bHarvard T\.?H\.? Chan School of Public Health\b/gi,
  /\bJohns Hopkins Bloomberg School of Public Health\b/gi,
  /\bSchool of Medicine\b/gi,

  // Generic patterns
  /\bUniversity of [A-Z][a-zA-Z-]+(?:\s+(?:at\s+)?[A-Z][a-zA-Z-]+)?\b/g,
  /\b[A-Z][a-zA-Z-]+\s+University\b/g,
  /\b[A-Z][a-zA-Z-]+\s+(?:State|Tech|Polytechnic)\s+University\b/g,
  /\b[A-Z][a-zA-Z-]+\s+College\b/g,
  /\b[A-Z][a-zA-Z-]+\s+Institute\s+of\s+Technology\b/g,
  /\bSchool\s+of\s+(?:Business|Medicine|Law|Engineering|Public Health)\b/gi,
];

function maskSchools(text: string): { text: string; replacements: PiiReplacement[] } {
  const replacements: PiiReplacement[] = [];
  let out = text;
  const seen = new Set<string>();

  for (const pattern of SCHOOL_PATTERNS) {
    out = out.replace(pattern, (match) => {
      const key = match.toLowerCase().trim();
      if (!seen.has(key)) {
        seen.add(key);
        replacements.push({
          category: "school",
          original: match,
          placeholder: "[SCHOOL]",
        });
      }
      return "[SCHOOL]";
    });
  }

  return { text: out, replacements };
}

// -- 3. Graduation years / date ranges ---------------------------------------
// Strips 4-digit years in contexts where they're load-bearing identity signals
// (graduation, date ranges in experience). Leaves "2025" alone when it's
// part of an achievement ("shipped in 2025").
function maskYears(text: string): { text: string; replacements: PiiReplacement[] } {
  const replacements: PiiReplacement[] = [];
  let out = text;

  // 1. "Class of YYYY" or "Graduated YYYY" or "YYYY present" or "YYYY YYYY"
  const patterns: RegExp[] = [
    /\b(Class of|Graduated|Expected)\s+(19|20)\d{2}\b/gi,
    /\b(19|20)\d{2}\s*[-–—]\s*(19|20)\d{2}\b/g, // "2020-2023"
    /\b(19|20)\d{2}\s*[-–—]\s*present\b/gi, // "2023-present"
    /\b(19|20)\d{2}\s*to\s*(19|20)\d{2}\b/gi,
    /\b(19|20)\d{2}\s*to\s*present\b/gi,
  ];

  for (const pat of patterns) {
    out = out.replace(pat, (match) => {
      replacements.push({
        category: "year",
        original: match,
        placeholder: "[DATE]",
      });
      return "[DATE]";
    });
  }

  return { text: out, replacements };
}

// -- 4. Emails ---------------------------------------------------------------
function maskEmails(text: string): { text: string; replacements: PiiReplacement[] } {
  const replacements: PiiReplacement[] = [];
  const out = text.replace(
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
    (match) => {
      replacements.push({
        category: "email",
        original: match,
        placeholder: "[EMAIL]",
      });
      return "[EMAIL]";
    },
  );
  return { text: out, replacements };
}

// -- 5. Phone numbers --------------------------------------------------------
function maskPhones(text: string): { text: string; replacements: PiiReplacement[] } {
  const replacements: PiiReplacement[] = [];
  // Various US phone formats; 7-11 digits with optional separators
  const out = text.replace(
    /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/g,
    (match) => {
      // Reject pure digit sequences that are obviously not phones (too short/long)
      const digits = match.replace(/\D/g, "");
      if (digits.length < 10 || digits.length > 11) return match;
      replacements.push({
        category: "phone",
        original: match,
        placeholder: "[PHONE]",
      });
      return "[PHONE]";
    },
  );
  return { text: out, replacements };
}

// -- 6. Addresses ------------------------------------------------------------
// Only strips obvious "City, ST" tails and full street addresses near the top
// of the document. Doesn't attempt deep address parsing.
const US_STATES =
  "AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY";

function maskAddresses(text: string): { text: string; replacements: PiiReplacement[] } {
  const replacements: PiiReplacement[] = [];
  let out = text;

  // Street addresses: "123 Main St", "4567 Elm Avenue, ..."
  out = out.replace(
    /\b\d{1,5}\s+[A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)*\s+(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Circle|Cir|Way|Place|Pl)\b\.?/gi,
    (match) => {
      replacements.push({
        category: "address",
        original: match,
        placeholder: "[ADDRESS]",
      });
      return "[ADDRESS]";
    },
  );

  // "City, ST" and "City, ST 12345"
  const cityStateRe = new RegExp(
    `\\b[A-Z][a-zA-Z]+(?:\\s+[A-Z][a-zA-Z]+)?,\\s+(?:${US_STATES})\\b(?:\\s+\\d{5}(?:-\\d{4})?)?`,
    "g",
  );
  out = out.replace(cityStateRe, (match) => {
    replacements.push({
      category: "address",
      original: match,
      placeholder: "[LOCATION]",
    });
    return "[LOCATION]";
  });

  return { text: out, replacements };
}

// -- 7. Gender pronouns ------------------------------------------------------
// Replace he/him/his/she/her/hers with they/them/their. Word boundaries only;
// never touch substrings inside real words ("here", "their", etc. are safe).
const PRONOUN_MAP: Record<string, string> = {
  he: "they",
  He: "They",
  HE: "THEY",
  him: "them",
  Him: "Them",
  HIM: "THEM",
  his: "their",
  His: "Their",
  HIS: "THEIR",
  she: "they",
  She: "They",
  SHE: "THEY",
  her: "them", // context-dependent; they/them is the safer replacement
  Her: "Them",
  HER: "THEM",
  hers: "theirs",
  Hers: "Theirs",
  HERS: "THEIRS",
};

function maskPronouns(text: string): { text: string; replacements: PiiReplacement[] } {
  const replacements: PiiReplacement[] = [];
  const pattern = /\b(he|He|HE|him|Him|HIM|his|His|HIS|she|She|SHE|her|Her|HER|hers|Hers|HERS)\b/g;
  let count = 0;
  const out = text.replace(pattern, (match) => {
    count++;
    return PRONOUN_MAP[match] ?? match;
  });
  if (count > 0) {
    replacements.push({
      category: "pronoun",
      original: `${count} gendered pronoun${count === 1 ? "" : "s"}`,
      placeholder: "(neutralized)",
    });
  }
  return { text: out, replacements };
}

// -- 8. URLs -----------------------------------------------------------------
function maskUrls(text: string): { text: string; replacements: PiiReplacement[] } {
  const replacements: PiiReplacement[] = [];
  const out = text.replace(/\bhttps?:\/\/[^\s<>"']+/g, (match) => {
    replacements.push({
      category: "url",
      original: match,
      placeholder: "[URL]",
    });
    return "[URL]";
  });
  return { text: out, replacements };
}

// -- 9. Date of birth --------------------------------------------------------
// "DOB: 01/15/1992", "Date of birth ...", "Born: ..."
function maskDobs(text: string): { text: string; replacements: PiiReplacement[] } {
  const replacements: PiiReplacement[] = [];
  const out = text.replace(
    /\b(DOB|Date\s+of\s+Birth|Born)\s*[:.]?\s*[\w\d\s\/,-]+?(?=\n|$|\.|,)/gi,
    (match) => {
      replacements.push({
        category: "dob",
        original: match,
        placeholder: "[DOB]",
      });
      return "[DOB]";
    },
  );
  return { text: out, replacements };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Run the full PII masking pipeline over a block of text.
 *
 * Returns the masked text plus a structured record of every replacement
 * so the UI can render a "what was stripped" audit trail.
 */
export function maskPii(
  text: string,
  options: PiiMaskOptions = DEFAULT_PII_OPTIONS,
): PiiMaskResult {
  const opts = { ...DEFAULT_PII_OPTIONS, ...options };
  const allReplacements: PiiReplacement[] = [];
  let current = text;

  const pipeline: Array<[keyof PiiMaskOptions, MaskerFn]> = [
    ["maskEmail", maskEmails],
    ["maskPhone", maskPhones],
    ["maskUrl", maskUrls],
    ["maskDob", maskDobs],
    ["maskName", maskNames],
    ["maskSchool", maskSchools],
    ["maskAddress", maskAddresses],
    ["maskYear", maskYears],
    ["maskPronoun", maskPronouns],
  ];

  for (const [flag, fn] of pipeline) {
    if (!opts[flag]) continue;
    const result = fn(current);
    current = result.text;
    allReplacements.push(...result.replacements);
  }

  // Collapse repeated [REDACTED] adjacent tokens into one for readability
  current = current.replace(/(?:\[REDACTED\]\s*){2,}/g, "[REDACTED] ");

  const counts: Record<PiiCategory, number> = {
    name: 0,
    school: 0,
    year: 0,
    email: 0,
    phone: 0,
    address: 0,
    pronoun: 0,
    url: 0,
    dob: 0,
  };
  for (const r of allReplacements) counts[r.category]++;

  return {
    maskedText: current,
    replacements: allReplacements,
    counts,
  };
}

// ---------------------------------------------------------------------------
// Bias delta — compare unmasked vs masked scores
// ---------------------------------------------------------------------------
export interface BiasDelta {
  overall: number;           // unmasked - masked, positive if masking hurt the score
  perSignal: {
    semantic: number;
    keyword: number;
    bullet: number;
    section: number;
    title: number;
    seniority: number;
    field: number;
  };
  flagged: boolean;          // true if |overall| > FLAG_THRESHOLD
  severity: "none" | "mild" | "moderate" | "severe";
}

export const FLAG_THRESHOLD = 10;

export function computeBiasDelta(
  unmasked: { overallScore: number; subScores: Record<string, number> },
  masked: { overallScore: number; subScores: Record<string, number> },
): BiasDelta {
  const overall = unmasked.overallScore - masked.overallScore;

  const perSignal = {
    semantic: (unmasked.subScores.semantic ?? 0) - (masked.subScores.semantic ?? 0),
    keyword: (unmasked.subScores.keyword ?? 0) - (masked.subScores.keyword ?? 0),
    bullet: (unmasked.subScores.bullet ?? 0) - (masked.subScores.bullet ?? 0),
    section: (unmasked.subScores.section ?? 0) - (masked.subScores.section ?? 0),
    title: (unmasked.subScores.title ?? 0) - (masked.subScores.title ?? 0),
    seniority: (unmasked.subScores.seniority ?? 0) - (masked.subScores.seniority ?? 0),
    field: (unmasked.subScores.field ?? 0) - (masked.subScores.field ?? 0),
  };

  const abs = Math.abs(overall);
  let severity: BiasDelta["severity"] = "none";
  if (abs >= 20) severity = "severe";
  else if (abs >= 10) severity = "moderate";
  else if (abs >= 5) severity = "mild";

  return {
    overall,
    perSignal,
    flagged: abs >= FLAG_THRESHOLD,
    severity,
  };
}
